import { Button, Chip, MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import React, { type FC, useState } from 'react'
import { FormFieldTitle, FormInputContainer } from './CustomersView.style'
import { type User } from '../../../interfaces/User'
import { useTranslation } from 'react-i18next'
import {
  AssignUserToCompanyAction,
  DeleteCompanyUserAction,
  OpenEditCompanyDialogAction
} from '../../../redux/admin/admin-companies-actions'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type SelectedOrganization, type UnassignedUser } from '../../../redux/admin/admin-companies-types'
import type { RootState } from '../../../redux/store'

interface CustomerUserProps {
  selectedOrganization: SelectedOrganization
}

const EMPTY_USERS = []

/**
 * Customer users management component (for admins).
 */
export const CustomerUsers: FC<CustomerUserProps> = ({
  selectedOrganization
}) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const unassignedUsers = useAppSelector<UnassignedUser[]>((state: RootState) => state.admin.unassignedUsers)
  const editedOrganizationUsers = useAppSelector<User[]>((state: RootState) =>
    state.admin.companies.find(it => it.id === selectedOrganization.id)?.users ?? EMPTY_USERS)

  const [userIdToAdd, setUserIdToAdd] = useState<string>('')

  const isCompanySelected =
        selectedOrganization.id != null && selectedOrganization.id !== ''

  function onSelectUserToAdd(e: SelectChangeEvent): void {
    e.preventDefault()

    if (e.target.value === '') {
      return
    }

    setUserIdToAdd(e.target.value)
  }

  function onClickAddUserToCompany(company: SelectedOrganization, userId: string): void {
    if (!isCompanySelected) {
      return
    }

    const assignedUser = unassignedUsers.find((unassignedUser) => unassignedUser.id === userId)
    if (assignedUser == null) {
      console.warn('Added unassigned user not found -> skipping addition')
      return
    }

    const msg: string = `${t('companyDetails.confirmAssignUser', {
            username: assignedUser.username,
            companyName: company.name
        })}`
    if (!window.confirm(msg)) {
      return
    }

    dispatch(AssignUserToCompanyAction({ companyId: company.id, userId }))

    const updatedUsers = editedOrganizationUsers.concat({
      ...assignedUser,
      systemAdmin: false
    })

    const updatedCompanyData = {
      ...selectedOrganization,
      users: updatedUsers
    }
    dispatch(OpenEditCompanyDialogAction({ company: updatedCompanyData })) // Refresh to show new user
    setUserIdToAdd('') // Clear input
  }

  function onClickDeleteUserFromCompany(
    company: SelectedOrganization,
    user: User
  ): void {
    if (!isCompanySelected) {
      return
    }

    const msg: string = `${t('companyDetails.confirmDeleteUser', { username: user.username })}`
    if (window.confirm(msg)) {
      dispatch(DeleteCompanyUserAction({
        companyId: company.id,
        userId: user.id
      }))
    }
  }

  return (
        <div>
            <FormFieldTitle>{t('companyDetails.users')}</FormFieldTitle>
            <FormInputContainer>
                <Select
                    id="user-email"
                    variant="outlined"
                    value={userIdToAdd}
                    disabled={!isCompanySelected}
                    onChange={onSelectUserToAdd}
                >
                    {unassignedUsers.map((user: UnassignedUser) => (
                        <MenuItem key={user.id} id={user.id} value={user.id}>
                            {user.username}
                        </MenuItem>
                    ))}
                </Select>
                <Button
                    variant="contained"
                    disabled={!isCompanySelected || userIdToAdd === ''}
                    onClick={() => {
                      onClickAddUserToCompany(selectedOrganization, userIdToAdd)
                    }}
                    color="primary"
                >
                    {t('companyDetails.addUser')}
                </Button>
            </FormInputContainer>
            {editedOrganizationUsers.map((user) => (
                <Chip
                    label={user.username}
                    key={user.id}
                    onDelete={() => {
                      onClickDeleteUserFromCompany(selectedOrganization, user)
                    }
                    }
                />
            ))}
        </div>
  )
}
